/**
 * Phase F — Occupied (Seated) variant.
 *
 * Pre-fills with current reservation data (decision #2).
 * Reserve-from-occupied behaves as same-party edit (decision #3).
 *
 * Buttons: Reserve (yellow) · Reassign (green) · Clear (grey)
 * APIs:    POST /api/tables/{code}/reserve-from-occupied
 *          POST /api/tables/{code}/reassign
 *          POST /api/tables/{code}/clear
 */
import { useState } from "react";
import DialogFieldGroup, {
  buildInitialValues,
  buildPayload,
} from "@/components/dialog/DialogFieldGroup";
import {
  useClearTable,
  useReassignTable,
  useReserveFromOccupied,
} from "@/hooks/useTableMutations";

export default function OccupiedForm({ table, onClose }) {
  const [values, setValues] = useState(buildInitialValues(table));
  const reserve = useReserveFromOccupied();
  const reassign = useReassignTable();
  const clear = useClearTable();

  const submitting = reserve.isPending || reassign.isPending || clear.isPending;

  const handleReserve = () => {
    const payload = buildPayload(values, { includeComments: true });
    reserve.mutate(
      { code: table.code, payload, version: table.version },
      { onSuccess: onClose },
    );
  };

  const handleReassign = () => {
    const payload = buildPayload(values, { includeComments: true });
    reassign.mutate(
      { code: table.code, payload, version: table.version },
      { onSuccess: onClose },
    );
  };

  const handleClear = () => {
    clear.mutate(
      { code: table.code, version: table.version },
      { onSuccess: onClose },
    );
  };

  return (
    <>
      <DialogFieldGroup
        values={values}
        setValues={setValues}
        showComment
        commentReadOnly
      />

      <div
        className="flex justify-end gap-3 px-5 py-4 border-t"
        style={{ borderColor: "#E5E7EB" }}
      >
        <button
          data-testid="dlg-btn-reserve"
          onClick={handleReserve}
          disabled={submitting}
          className="px-4 py-2 rounded-md font-semibold text-sm disabled:opacity-50 transition-opacity"
          style={{ backgroundColor: "#F5C518", color: "#1A1A1A" }}
        >
          {reserve.isPending ? "…" : "Reserve"}
        </button>
        <button
          data-testid="dlg-btn-reassign"
          onClick={handleReassign}
          disabled={submitting}
          className="px-4 py-2 rounded-md font-semibold text-sm text-white disabled:opacity-50 transition-opacity"
          style={{ backgroundColor: "#16A34A" }}
        >
          {reassign.isPending ? "…" : "Reassign"}
        </button>
        <button
          data-testid="dlg-btn-clear"
          onClick={handleClear}
          disabled={submitting}
          className="px-4 py-2 rounded-md font-semibold text-sm text-white disabled:opacity-50 transition-opacity"
          style={{ backgroundColor: "#888888" }}
        >
          {clear.isPending ? "…" : "Clear"}
        </button>
      </div>
    </>
  );
}
